import type { Dispatch, SetStateAction } from "react"

import { useTranslation } from "react-i18next"
import { type UseMutationResult, useMutation } from "react-query"
import { useSelector } from "react-redux"

import { NotificationUtils } from "@synapse-analytics/synapse-ui"
import { AxiosError, type AxiosResponse } from "axios"
import { v4 as uuidv4 } from "uuid"

import { KonanAPI } from "../../../services/KonanAPI"
import { queryClient } from "../../../store/QueryClientStore"
import type { RootState } from "../../../store/ReduxStore"
import type { CreateScorecardSetRequest } from "../../../types/custom/projects"
import type { CreateRulesetRequest, CreateTaglistRequest } from "../../../types/custom/rules"
import { DataFileGroup } from "../../../types/generated/api/DataFileGroup"
import type { Label } from "../../../types/generated/api/Label"
import type { RuleSetList } from "../../../types/generated/api/RuleSetList"
import { ScoreCardCreateUpdateRequest } from "../../../types/generated/api/ScoreCardCreateUpdateRequest"
import type { ScriptGroupRetrieve } from "../../../types/generated/api/ScriptGroupRetrieve"
import { useIsPermitted } from "../../../utils/PermissionsHelpers"
import { RulesetCard } from "../../Ruleset/components/RulesetCard"
import { Scorecardset } from "../../ScoreCardSets/Scorecardset"
import type { CreateScoreCardRequest } from "../../Scorecards/Interfaces"
import { Scorecard } from "../../Scorecards/components/Scorecard"
import type { CreateScriptRequest } from "../../Scripts/Interfaces"
import { SingleScript } from "../../Scripts/ScriptDialog"
import { TaglistCard } from "../../Taglist"
import { MessageItem } from "../helpers"
import type {
  ComponentAction,
  ComponentActions,
  IMessage,
  LabelComponent,
  RuleSetComponent,
  ScoreCardComponent,
  ScoreCardSetComponent,
  ScriptComponent,
  TagListComponent,
} from "../interfaces"

type LabelPayload = {
  project_uuid: string
  name: string
}

function useCreateLabel(): UseMutationResult<AxiosResponse<Label>, AxiosError, LabelPayload> {
  return useMutation<AxiosResponse<Label>, AxiosError, LabelPayload>(KonanAPI.CreateLabel, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(["label-list"])
    },
  })
}

function useCreateRuleset(): UseMutationResult<AxiosResponse, AxiosError, CreateRulesetRequest> {
  return useMutation<AxiosResponse, AxiosError, CreateRulesetRequest>(KonanAPI.createRuleset, {
    mutationKey: "ruleset-create",
  })
}

function useCreateTaglist(): UseMutationResult<AxiosResponse, AxiosError, CreateTaglistRequest> {
  return useMutation<AxiosResponse, AxiosError, CreateTaglistRequest>(KonanAPI.createTaglist, {
    mutationKey: "taglist-create",
  })
}

function useCreateScoreCard(): UseMutationResult<AxiosResponse, AxiosError, CreateScoreCardRequest> {
  return useMutation<AxiosResponse, AxiosError, CreateScoreCardRequest>(KonanAPI.createScorecard, {
    mutationKey: "scorecard-create",
  })
}

function useCreateScoreCardSet(): UseMutationResult<AxiosResponse, AxiosError, CreateScorecardSetRequest> {
  return useMutation<AxiosResponse, AxiosError, CreateScorecardSetRequest>(KonanAPI.createScorecardSet, {
    mutationKey: "createScorecardset",
  })
}

function useCreateFile(): UseMutationResult<
  DataFileGroup,
  AxiosError,
  { file: File; projectId: string; type: DataFileGroup.type }
> {
  return useMutation<DataFileGroup, AxiosError, { file: File; projectId: string; type: DataFileGroup.type }>(
    (data) => {
      return KonanAPI.uploadDataFile({
        project_uuid: data.projectId,
        file: data.file,
        type: data.type,
      })
    },
    {
      mutationKey: "uploadScriptFile",
    },
  )
}

function useCreateScript(): UseMutationResult<AxiosResponse<ScriptGroupRetrieve>, AxiosError, CreateScriptRequest> {
  return useMutation<AxiosResponse<ScriptGroupRetrieve>, AxiosError, CreateScriptRequest>(KonanAPI.createScript)
}

function useCreateScriptHandler(): {
  createScript: (payload: ScriptComponent) => Promise<AxiosResponse<ScriptGroupRetrieve>>
} {
  const { t } = useTranslation("KonanAssistant")
  const currentProject = useSelector((state: RootState) => state.project.currentProject)
  const { mutateAsync: createFile } = useCreateFile()
  const { mutateAsync: createScriptMutation } = useCreateScript()

  const createScript = async (payload: ScriptComponent): Promise<AxiosResponse<ScriptGroupRetrieve>> => {
    if (!currentProject?.uuid) {
      throw new Error(t("autoRun.noCurrentProject"))
    }

    const dataFile = await createFile({
      file: new File([payload.body.file], payload.body.name + "_" + uuidv4() + ".py", { type: "text/plain" }),
      projectId: currentProject?.uuid,
      type: DataFileGroup.type.SCRIPT,
    })

    const script = await createScriptMutation({
      project_uuid: currentProject?.uuid,
      name: payload.body.name,
      file: dataFile.uuid,
      is_draft: false,
    })

    return script
  }

  return { createScript }
}

function useCreateLabelHandler(): {
  createLabel: (payload: LabelComponent) => Promise<{ uuid: string; name: string }>
} {
  const { t } = useTranslation("KonanAssistant")
  const currentProject = useSelector((state: RootState) => state.project.currentProject)
  const createLabelMutation = useCreateLabel()

  const createLabel = async (payload: LabelComponent): Promise<{ uuid: string; name: string }> => {
    if (!currentProject?.uuid) {
      throw new Error(t("autoRun.noCurrentProject"))
    }

    const label = await createLabelMutation.mutateAsync({
      project_uuid: currentProject.uuid,
      name: payload.body.name,
    })

    return { uuid: label.data.uuid, name: label.data.name }
  }

  return { createLabel }
}

function useCreateRulesetHandler(): {
  createRuleset: (
    payload: RuleSetComponent,
    componentsIds: Record<string, { uuid: string; name: string; type: string }>,
  ) => Promise<{ uuid: string; name: string }>
  canShowDetails: boolean
} {
  const { t } = useTranslation("KonanAssistant")
  const currentProject = useSelector((state: RootState) => state.project.currentProject)
  const createRulesetMutation = useCreateRuleset()

  // check permissions
  const permissions = useSelector((state: RootState) => state.auth.permissions)
  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const canShowDetails = useIsPermitted("Retrieve ruleset", permissions.konan, flattenedKonanPermissions)

  const createRuleset = async (
    payload: RuleSetComponent,
    componentsIds: Record<string, { uuid: string; name: string; type: string }>,
  ): Promise<{ uuid: string; name: string }> => {
    if (!currentProject?.uuid) {
      throw new Error(t("autoRun.noCurrentProject"))
    }

    const ruleset = await createRulesetMutation.mutateAsync({
      projectId: currentProject?.uuid,
      name: payload.body.name,
      rules: payload.body.rules.map((rule) => {
        let returnLabel = ""

        const regex = /^\$REQUEST_STATE\$\[(\d+)\]$/

        const match = rule.return_label.uuid.match(regex)
        if (match) {
          const number = parseInt(match[1] as string, 10)
          returnLabel = componentsIds[`${number}`]?.uuid as string
        } else {
          returnLabel = rule.return_label.uuid
        }

        return { ...rule, return_label: returnLabel }
      }),
    })

    return { uuid: ruleset.data.uuid, name: ruleset.data.name }
  }

  return { createRuleset, canShowDetails }
}

function useCreateTaglistHandler(): {
  createTaglist: (payload: TagListComponent) => Promise<{ uuid: string; name: string }>
  canShowDetails: boolean
} {
  const { t } = useTranslation("KonanAssistant")
  const currentProject = useSelector((state: RootState) => state.project.currentProject)
  const createTaglistMutation = useCreateTaglist()

  // check permissions
  const permissions = useSelector((state: RootState) => state.auth.permissions)
  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const canShowDetails = useIsPermitted("Retrieve taglist", permissions.konan, flattenedKonanPermissions)

  const createTaglist = async (payload: TagListComponent): Promise<{ uuid: string; name: string }> => {
    if (!currentProject?.uuid) {
      throw new Error(t("autoRun.noCurrentProject"))
    }

    const taglist = await createTaglistMutation.mutateAsync({
      projectId: currentProject?.uuid,
      name: payload.body.name,
      rules: payload.body.rules,
    })

    return { uuid: taglist.data.uuid, name: taglist.data.name }
  }

  return { createTaglist, canShowDetails }
}

function useCreateScorecardHandler(): {
  createScorecard: (payload: ScoreCardComponent) => Promise<{ uuid: string; name: string }>
} {
  const { t } = useTranslation("KonanAssistant")
  const currentProject = useSelector((state: RootState) => state.project.currentProject)
  const createScorecardMutation = useCreateScoreCard()

  const createScorecard = async (payload: ScoreCardComponent): Promise<{ uuid: string; name: string }> => {
    if (!currentProject?.uuid) {
      throw new Error(t("autoRun.noCurrentProject"))
    }

    const scorecard = await createScorecardMutation.mutateAsync({
      projectId: currentProject?.uuid,
      name: payload.body.name,
      rules: payload.body.rules,
      format: ScoreCardCreateUpdateRequest.format.LIST,
    })

    return { name: scorecard.data.name, uuid: scorecard.data.uuid }
  }

  return { createScorecard }
}

function useCreateScorecardSetHandler(): {
  createScorecardSet: (
    payload: ScoreCardSetComponent,
    componentsIds: Record<string, { uuid: string; name: string; type: string }>,
  ) => Promise<{ uuid: string; name: string }>
} {
  const { t } = useTranslation("KonanAssistant")
  const currentProject = useSelector((state: RootState) => state.project.currentProject)
  const createScorecardSetMutation = useCreateScoreCardSet()

  const createScorecardSet = async (
    payload: ScoreCardSetComponent,
    componentsIds: Record<string, { uuid: string; name: string; type: string }>,
  ): Promise<{ uuid: string; name: string }> => {
    if (!currentProject?.uuid) {
      throw new Error(t("autoRun.noCurrentProject"))
    }

    const scorecardset = await createScorecardSetMutation.mutateAsync({
      projectId: currentProject?.uuid,
      name: payload.body.name,
      calculation_method: payload.body.calculation_method ?? "VALUE_BY_WEIGHT",
      scorecards: payload.body.scorecards.map((scorecard) => {
        let uuid = ""

        const regex = /^\$REQUEST_STATE\$\[(\d+)\]$/

        const match = scorecard.uuid.match(regex)
        if (match) {
          const number = parseInt(match[1] as string, 10)
          uuid = componentsIds[`${number}`]?.uuid as string
        } else {
          uuid = scorecard.uuid
        }

        return { ...scorecard, uuid: uuid }
      }),
    })

    return { name: scorecardset.data.name, uuid: scorecardset.data.uuid }
  }

  return { createScorecardSet }
}

export function useCreateComponentsHandler(props: {
  setIsCreating: Dispatch<SetStateAction<boolean>>
  setMessages: Dispatch<SetStateAction<IMessage[]>>
}): {
  createComponents: (payload: ComponentActions) => Promise<void>
} {
  const { t } = useTranslation("KonanAssistant")
  const { setMessages, setIsCreating } = props

  const currentUser = useSelector((state: RootState) => state.auth.email) as string

  const { createLabel } = useCreateLabelHandler()
  const { createRuleset, canShowDetails: canShowRulesetDetails } = useCreateRulesetHandler()
  const { createTaglist, canShowDetails: canShowTaglistDetails } = useCreateTaglistHandler()
  const { createScorecard } = useCreateScorecardHandler()
  const { createScorecardSet } = useCreateScorecardSetHandler()
  const { createScript } = useCreateScriptHandler()

  //  TODO:: check if we can convert to state
  const componentsIds: Record<string, { uuid: string; name: string; type: string }> = {}

  const createComponents = async (payload: ComponentActions): Promise<void> => {
    const data = payload

    try {
      for (const item in data) {
        let componentResponse:
          | {
              uuid: string
              name: string
              type: string
              error?: string
            }
          | undefined = undefined

        // workaround for the scripts
        let scriptResponse: AxiosResponse<ScriptGroupRetrieve> | undefined = undefined

        const dataItem = data[item] as ComponentAction

        // create component
        if (dataItem.component_name === "Label") {
          const label = await createLabel(dataItem)
          componentResponse = { uuid: label.uuid, name: label.name, type: dataItem.component_name }
        } else if (dataItem.component_name === "RuleSet") {
          const ruleset = await createRuleset(dataItem, componentsIds)
          componentResponse = { uuid: ruleset.uuid, name: ruleset.name, type: dataItem.component_name }
        } else if (dataItem.component_name === "TagList") {
          const taglist = await createTaglist(dataItem)
          componentResponse = { uuid: taglist.uuid, name: taglist.name, type: dataItem.component_name }
        } else if (dataItem.component_name === "ScoreCard") {
          const scorecard = await createScorecard(dataItem)
          componentResponse = { uuid: scorecard.uuid, name: scorecard.name, type: dataItem.component_name }
        } else if (dataItem.component_name === "ScoreCardSet") {
          const scorecardset = await createScorecardSet(dataItem, componentsIds)
          componentResponse = { uuid: scorecardset.uuid, name: scorecardset.name, type: dataItem.component_name }
        } else if (dataItem.component_name === "Script") {
          scriptResponse = await createScript(dataItem)
        }

        // Create title
        const actionType = dataItem?.action_type
        const componentName = dataItem?.component_name

        const title = `${
          actionType ? actionType.charAt(0).toUpperCase() + actionType.slice(1) : t("autoRun.unknown")
        } ${componentName ?? t("autoRun.unknownComponent")}`

        // edit loading to confirm/show error
        // TODO:: refactor this and show it as a message instead of a toast
        if (componentResponse?.error) NotificationUtils.toast(componentResponse.error, { severity: "error" })

        componentsIds[`${item}`] = {
          uuid: componentResponse?.uuid as string,
          name: componentResponse?.name as string,
          type: componentResponse?.type as string,
        }

        // add component to view
        if (dataItem.component_name === "Label")
          setMessages((prev) => [
            ...prev,
            {
              sender: "bot",
              content: <MessageItem title={title} comment={dataItem.comment} componentName={dataItem.component_name} />,
            },
          ])
        else if (dataItem.component_name === "RuleSet")
          setMessages((prev) => [
            ...prev,
            {
              sender: "bot",
              content: (
                <MessageItem
                  title={title}
                  comment={dataItem.comment}
                  componentName={dataItem.component_name}
                  component={
                    <RulesetCard
                      metaData={{ name: dataItem.body.name, created_by: currentUser } as RuleSetList}
                      canShowDetails={canShowRulesetDetails}
                      uuid={componentResponse?.uuid as string}
                      isAudit={true}
                    />
                  }
                />
              ),
            },
          ])
        else if (dataItem.component_name === "TagList")
          setMessages((prev) => [
            ...prev,
            {
              sender: "bot",
              content: (
                <MessageItem
                  title={title}
                  comment={dataItem.comment}
                  componentName={dataItem.component_name}
                  component={
                    <TaglistCard
                      metaData={{ name: dataItem.body.name, created_by: currentUser } as RuleSetList}
                      canShowDetails={canShowTaglistDetails}
                      uuid={componentResponse?.uuid as string}
                      isAudit={true}
                    />
                  }
                />
              ),
            },
          ])
        else if (dataItem.component_name === "ScoreCard")
          setMessages((prev) => [
            ...prev,
            {
              sender: "bot",
              content: (
                <MessageItem
                  title={title}
                  comment={dataItem.comment}
                  componentName={dataItem.component_name}
                  component={
                    <Scorecard
                      uuid={componentResponse?.uuid as string}
                      isAudit={true}
                      scorecard={{
                        uuid: componentResponse?.uuid as string,
                        created_by: currentUser,
                        name: componentResponse?.name as string,
                        created_at: "",
                        format: undefined,
                      }}
                    />
                  }
                />
              ),
            },
          ])
        else if (dataItem.component_name === "ScoreCardSet")
          setMessages((prev) => [
            ...prev,
            {
              sender: "bot",
              content: (
                <MessageItem
                  title={title}
                  comment={dataItem.comment}
                  componentName={dataItem.component_name}
                  component={
                    <Scorecardset
                      metaData={{ created_by: currentUser, name: dataItem.body.name } as RuleSetList}
                      uuid={componentResponse?.uuid}
                    />
                  }
                />
              ),
            },
          ])
        else if (dataItem.component_name === "Script")
          setMessages((prev) => [
            ...prev,
            {
              sender: "bot",
              content: (
                <MessageItem
                  title={title}
                  comment={dataItem.comment}
                  componentName={dataItem.component_name}
                  component={
                    <SingleScript
                      scriptData={scriptResponse?.data}
                      isScriptLoading={false}
                      shouldShowBuildingState={false}
                    />
                  }
                />
              ),
            },
          ])
      }
    } catch {
      NotificationUtils.toast(t("autoRun.errorDuringCreation"), { severity: "error" })
    } finally {
      setIsCreating(false)
    }
  }

  return { createComponents }
}
